import { useCallback, useEffect, useRef, type RefObject } from 'react';

/**
 * How near an edge the pointer has to come before the list starts moving.
 * Wide enough to hit without aiming, narrow enough that the middle of a card
 * is still a still place to hover.
 */
const EDGE_PX = 48;

/** Pixels per frame at the very edge, and at the far side of the edge band. */
const MAX_SPEED_PX = 14;
const MIN_SPEED_PX = 2;

/** Overflow values that make an element a real scroll port. */
const SCROLLING_OVERFLOW = new Set(['auto', 'scroll', 'overlay']);

/** Does this element scroll vertically, right now, with content to scroll? */
function isScrollPort(node: HTMLElement): boolean {
  const { overflowY } = window.getComputedStyle(node);
  if (!SCROLLING_OVERFLOW.has(overflowY)) return false;
  return node.scrollHeight > node.clientHeight + 1;
}

/**
 * The nearest ancestor that actually scrolls. Found by walking up rather than
 * by class name: the panel is mounted inside the Edit feed dialog today, and
 * whichever box ends up holding the scrollbar is the one to move.
 */
function scrollPortOf(node: HTMLElement | null): HTMLElement | null {
  let current = node?.parentElement ?? null;
  while (current) {
    if (isScrollPort(current)) return current;
    current = current.parentElement;
  }
  return null;
}

/** Faster the closer to the edge; past it, flat out. */
function speedFor(distance: number): number {
  const clamped = Math.max(0, Math.min(distance, EDGE_PX));
  return MIN_SPEED_PX + (MAX_SPEED_PX - MIN_SPEED_PX) * (1 - clamped / EDGE_PX);
}

export interface DragAutoScroll {
  /** Call when a drag starts. Latches on to the scroll port and listens. */
  start: () => void;
  /** Call on drop, dragend, or anything that ends the gesture. Idempotent. */
  stop: () => void;
}

/**
 * Edge auto-scroll for a native HTML5 drag.
 *
 * The platform gives a dragging pointer no scrolling of its own: a list taller
 * than its scroll port can be dragged *at* but never dragged *past*, and the
 * wheel is dead while a drag is in progress. So the drag has to do the
 * scrolling — while a row is in hand, the pointer inside {@link EDGE_PX} of the
 * port's top or bottom moves it, at a speed that rises the closer it gets.
 *
 * The pointer is read from a `dragover` listener on the scroll port itself
 * rather than from the dragged rows, so it keeps working over the gaps between
 * cards and over anything else the port happens to hold. `dragover` fires
 * whether or not the element under the pointer is a drop target, so this needs
 * no cooperation from the cards; a `requestAnimationFrame` loop does the
 * moving, because `dragover` alone is too coarse to scroll smoothly by.
 *
 * @param anchorRef Any node inside the scroll port — its own root will do.
 */
export function useDragAutoScroll(anchorRef: RefObject<HTMLElement | null>): DragAutoScroll {
  const portRef = useRef<HTMLElement | null>(null);
  const pointerYRef = useRef<number | null>(null);
  const frameRef = useRef<number | null>(null);
  const listenerRef = useRef<((event: DragEvent) => void) | null>(null);

  const stop = useCallback(() => {
    if (frameRef.current !== null) {
      window.cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    const port = portRef.current;
    const listener = listenerRef.current;
    if (port && listener) port.removeEventListener('dragover', listener);
    listenerRef.current = null;
    portRef.current = null;
    pointerYRef.current = null;
  }, []);

  const start = useCallback(() => {
    stop();
    const port = scrollPortOf(anchorRef.current);
    if (!port) return;

    // `dragover` fires on whatever is under the pointer whether or not it will
    // take a drop, so listening on the port alone covers the cards, the gaps
    // between them and everything else the dialog is holding.
    const listener = (event: DragEvent) => {
      pointerYRef.current = event.clientY;
    };
    port.addEventListener('dragover', listener);
    portRef.current = port;
    listenerRef.current = listener;

    /** One frame: measure the pointer against the port's edges and move it. */
    function step(scroller: HTMLElement) {
      const pointerY = pointerYRef.current;
      if (pointerY !== null) {
        const box = scroller.getBoundingClientRect();
        const fromTop = pointerY - box.top;
        const fromBottom = box.bottom - pointerY;
        if (fromTop < EDGE_PX) scroller.scrollTop -= speedFor(fromTop);
        else if (fromBottom < EDGE_PX) scroller.scrollTop += speedFor(fromBottom);
      }
      // Kept running for the length of the gesture: the pointer can leave the
      // edge band and come back, and only `stop` ends it.
      frameRef.current = window.requestAnimationFrame(() => step(scroller));
    }

    frameRef.current = window.requestAnimationFrame(() => step(port));
  }, [anchorRef, stop]);

  // A drag interrupted by the panel unmounting — the dialog closed mid-gesture
  // — leaves neither a listener nor a frame behind.
  useEffect(() => stop, [stop]);

  return { start, stop };
}
